// utils/driveSync.js - Google Drive 단일 스냅샷 업/다운로드 (GIS 사용)
// appDataFolder에 fcfunnel-snapshot.bin 1개만 관리, gapi 미사용

import { GOOGLE_OAUTH_CLIENT_ID } from '../config';
import { backupAllData, restoreFromBackup } from './idb';

const SCOPES = 'https://www.googleapis.com/auth/drive.appdata';
const SNAPSHOT_FILENAME = 'fcfunnel-snapshot.bin';

let accessTokenCache = null;

async function loadGis() {
    if (window.google?.accounts?.oauth2) return;
    await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true; s.defer = true;
        s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
    });
}

async function getAccessToken(interactive = true, forceConsent = false) {
    await loadGis();
    if (!GOOGLE_OAUTH_CLIENT_ID) throw new Error('GOOGLE_OAUTH_CLIENT_ID가 설정되지 않았습니다.');
    if (accessTokenCache) return accessTokenCache;

    // GIS 토큰 클라이언트
    const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_OAUTH_CLIENT_ID,
        scope: SCOPES,
        // forceConsent=true일 때 동의 화면 강제 표시
        prompt: forceConsent ? 'consent' : (interactive ? '' : 'none'),
        callback: (resp) => { /* no-op, we resolve in requestAccessToken */ }
    });

    const token = await new Promise((resolve, reject) => {
        let settled = false;
        const timer = setTimeout(() => {
            if (settled) return;
            const err = new Error('사용자 상호작용이 취소되었거나 시간이 초과되었습니다.');
            // 사용자 취소로 간주
            err.code = 'user_cancelled';
            settled = true;
            reject(err);
        }, 20000); // 20초 타임아웃

        client.callback = (resp) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if (resp && resp.access_token) return resolve(resp.access_token);
            const err = new Error(resp?.error || '토큰 발급 실패');
            if (resp?.error === 'access_denied' || resp?.error === 'popup_closed') {
                err.code = 'user_cancelled';
            }
            reject(err);
        };
        try {
            client.requestAccessToken();
        } catch (e) {
            if (!settled) {
                settled = true;
                clearTimeout(timer);
                reject(e);
            }
        }
    });

    accessTokenCache = token;
    // 50분 후 만료 가정, 캐시 제거
    setTimeout(() => { accessTokenCache = null; }, 50 * 60 * 1000);
    return token;
}

async function driveListSnapshot(token) {
    const params = new URLSearchParams({
        spaces: 'appDataFolder',
        q: `name='${SNAPSHOT_FILENAME}' and trashed=false`,
        orderBy: 'modifiedTime desc',
        pageSize: '1',
        fields: 'files(id,name,modifiedTime)'
    });
    const res = await fetch(`https://www.googleapis.com/drive/v3/files?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        const err = new Error('Drive 파일 조회 실패');
        err._status = res.status;
        err._body = text;
        throw err;
    }
    return res.json();
}

export async function driveUploadSnapshot() {
    let token = await getAccessToken(true);

    // 1) 로컬 스냅샷 생성
    const { success, backup, error } = await backupAllData();
    if (!success) throw error || new Error('백업 생성 실패');

    // 3) 멀티파트 업로드 바디 구성
    const boundary = 'fcfunnel_boundary_' + Math.random().toString(36).slice(2);
    const delimiter = `\r\n--${boundary}\r\n`;
    const closeDelim = `\r\n--${boundary}--`;

    const metadata = { name: SNAPSHOT_FILENAME, parents: ['appDataFolder'] };
    const body =
        delimiter +
        'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
        JSON.stringify(metadata) +
        delimiter +
        'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
        JSON.stringify(backup) +
        closeDelim;

    const url = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';

    let res = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': `multipart/related; boundary=${boundary}`
        },
        body
    });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        const err = new Error('업로드 실패');
        err._status = res.status;
        err._body = text;
        throw err;
    }
    return { success: true };
}

export async function driveDownloadSnapshot() {
    let token = await getAccessToken(true);

    // 1) 파일 조회
    let list;
    try {
        list = await driveListSnapshot(token);
    } catch (e) {
        if (e?._status === 403) {
            accessTokenCache = null;
            token = await getAccessToken(true, true);
            list = await driveListSnapshot(token);
        } else {
            throw e;
        }
    }
    const file = list?.files?.[0];
    if (!file) throw new Error('드라이브에 스냅샷이 없습니다. 먼저 업로드를 실행하세요.');

    // 2) 다운로드
    const res = await fetch(`https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        const err = new Error('다운로드 실패');
        err._status = res.status;
        err._body = text;
        throw err;
    }
    const backup = await res.json();

    // 3) 복원
    const restored = await restoreFromBackup(backup);
    if (!restored.success) throw restored.error || new Error('복원 실패');
    return { success: true };
}


